import { checkQuotaLimit, safeGetSession, supabaseData } from './supabase';
import { getAccessToken } from './authStore';
import { getWriteUserId } from './dataScope';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL as string | undefined;

if (!apiBaseUrl) {
  // On force la config explicite pour éviter les mauvaises surprises
  // Exemple attendu en dev :
  // REACT_APP_API_BASE_URL=https://uuipwrmvs8.execute-api.eu-west-3.amazonaws.com/dev/api
  throw new Error(
    'Missing REACT_APP_API_BASE_URL environment variable. ' +
    'Please set it to your API Gateway base URL, e.g. https://.../dev/api'
  );
}

export interface CreateUploadUrlParams {
  entityType: string; // ex: "project", "lead"
  entityId: string;   // ex: UUID projet
  ownerUserId: string; // id de l'utilisateur propriétaire
  file: File;
}

export interface CreateUploadUrlResponse {
  fileId: string;
  uploadUrl: string;
  bucket: string;
  key: string;
}

async function jsonFetch<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  // Prefer in-memory token (fed by AuthContext). If missing, fall back once to safeGetSession.
  const token =
    getAccessToken() ||
    (await safeGetSession(4000)).data.session?.access_token ||
    undefined;
  if (!token) {
    throw new Error('Votre session a expiré. Veuillez vous reconnecter et réessayer.');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> || {}),
  };

  headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(input, {
    ...init,
    headers,
  });

  if (!res.ok) {
    let message = `Request failed with status ${res.status}`;
    try {
      const body = await res.json();
      if (body?.message) {
        message = body.message;
      }
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  return res.json() as Promise<T>;
}

export const filesService = {
  /**
   * Étape 1 : obtenir une URL pré-signée pour uploader un fichier vers S3.
   * Ne fait PAS le PUT lui-même, seulement la négociation avec la Lambda.
   */
  async createUploadUrl(params: CreateUploadUrlParams): Promise<CreateUploadUrlResponse> {
    const { entityType, entityId, ownerUserId, file } = params;

    // Check storage quota limit before creating upload URL (bytes = File.size)
    const quotaCheck = await checkQuotaLimit('storage', 1, file.size);
    if (!quotaCheck.allowed) {
      const error = new Error('QUOTA_LIMIT');
      (error as any).error = 'QUOTA_LIMIT';
      (error as any).quotaInfo = quotaCheck;
      throw error;
    }

    const body = {
      entityType,
      entityId,
      ownerUserId,
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      size: file.size,
    };

    return jsonFetch<CreateUploadUrlResponse>(`${apiBaseUrl}/files/upload-url`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * Étape 2 : uploader réellement le fichier sur l'URL pré-signée (direct S3).
   */
  async uploadFileToSignedUrl(uploadUrl: string, file: File): Promise<void> {
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      // Important : ne pas sur-forcer Content-Type ici, S3 se base sur celui
      // utilisé lors de la génération de l'URL pré-signée.
    });

    if (!res.ok) {
      throw new Error(`Failed to upload file to storage (status ${res.status})`);
    }
  },

  /**
   * Étape 3 : confirmer côté backend que l'upload est terminé.
   */
  async confirmUpload(fileId: string): Promise<void> {
    await jsonFetch<{ message: string }>(`${apiBaseUrl}/files/${encodeURIComponent(fileId)}/confirm`, {
      method: 'POST',
    });
  },

  /**
   * Obtenir l'URL de visualisation d'un fichier (URL CloudFront signée ou S3 en fallback).
   * Nécessite le JWT ; l'URL retournée expire après un délai côté CDN.
   */
  async getViewUrl(fileId: string): Promise<string> {
    const response = await jsonFetch<{ viewUrl: string; fileName: string; mimeType: string }>(
      `${apiBaseUrl}/files/${encodeURIComponent(fileId)}/view`,
      { method: 'GET' }
    );
    return response.viewUrl;
  },

  /**
   * Supprimer un fichier (S3 + Supabase) via la Lambda.
   */
  async deleteFile(fileId: string): Promise<void> {
    await jsonFetch<{ message: string }>(
      `${apiBaseUrl}/files/${encodeURIComponent(fileId)}`,
      { method: 'DELETE' }
    );
  },

  /**
   * Delete all files linked to an entity (project docs, Drive links, pending uploads).
   * Uses the same per-file Lambda path so S3 + DB stay in sync and storage quota drops.
   * Best-effort: logs failures and continues so the parent entity can still be removed.
   */
  async deleteFilesForEntity(entityType: string, entityId: string): Promise<void> {
    const userId = getWriteUserId();
    if (!userId || !entityType || !entityId) return;

    const { data, error } = await supabaseData
      .from('files')
      .select('id')
      .eq('owner_user_id', userId)
      .eq('entity_type', entityType)
      .eq('entity_id', entityId);

    if (error) {
      console.error('Error listing files for entity cleanup:', error);
      return;
    }

    const ids = (data || []).map((row) => row.id as string).filter(Boolean);
    if (ids.length === 0) return;

    const results = await Promise.allSettled(
      ids.map((id) => filesService.deleteFile(id))
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      console.error(
        `Failed to delete ${failed}/${ids.length} file(s) for ${entityType}/${entityId}`
      );
    }
  },
};


